import React, { useMemo } from 'react';
import { AgGrid, filters } from '../../../components/AgGrid';
import VirtualOrderPdfCellRenderer from './VirtualOrderPdfCellRenderer';
import BetterviewLinkCellRenderer from './BetterviewLinkCellRenderer';
import VirtualOrderConversionCellRenderer from './VirtualOrderConversionCellRenderer';
import QuickVueVirtualToInvisionConversionButton from '../Buttons/QuickVueVirtualToInvisionConversionButton';

const BetterviewGrid = ({
  gridData,
  onGridReady,
  gridApi,
  conversionOrders,
  onOpenConversionForm,
  onHideConversionForm,
  customActionsContainer,
}) => {
  const columnDefinitions = useMemo(() => [
    {
      checkboxSelection: ({ data }) => !data.childOrderLineId,
      showDisabledCheckboxes: true,
      headerCheckboxSelection: true,
      headerName: '',
      filter: false,
      sort: false,
      width: 75,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'PDF Report',
      field: 'pdf',
      filter: false,
      width: 150,
      cellRenderer: 'VirtualOrderPdfCellRenderer',
      cellClass: 'ag-cell-full-height',
    },
    {
      headerName: 'Betterview',
      field: 'betterview',
      filter: false,
      width: 100,
      cellRenderer: 'BetterviewLinkCellRenderer',
      cellClass: 'ag-cell-full-height',
    },
    {
      headerName: 'Virtual Order Conversion',
      field: 'conversion',
      width: 100,
      cellRenderer: 'VirtualOrderConversionCellRenderer',
      cellClass: 'ag-cell-full-height',
      filter: false,
      sort: false,
      cellRendererParams: {
        onClick: ({ data }) => {
          onOpenConversionForm({ data });
        },
      },
    },
    {
      headerName: 'QV Link Order Created',
      field: 'childOrderCreated',
      valueFormatter: params => (params.value == 0 ? 'No' : 'Yes'),
      filter: filters.set,
    },
    {
      headerName: 'Policy',
      field: 'policy',
    },
    {
      headerName: 'Insured Name',
      field: 'policyHolderName',
    },
    {
      headerName: 'Insured Address',
      field: 'policyHolderAddress',
    },
    {
      headerName: 'City',
      field: 'policyHolderCity',
      filter: filters.set,
    },
    {
      headerName: 'State',
      field: 'policyHolderState',
      filter: filters.set,
    },
    {
      headerName: 'Zip',
      field: 'policyHolderZip',
      filter: filters.number,
    },
    {
      headerName: 'County',
      field: 'policyHolderCounty',
      filter: filters.set,
    },
    {
      headerName: 'Data Hit',
      field: 'dataHit',
      filter: filters.set,
    },
    {
      headerName: 'Imagery Available',
      field: 'imageryAvailable',
      filter: filters.set,
    },
    {
      headerName: 'Image Capture Date',
      field: 'imageCaptureDate',
      filter: filters.date,
    },
    {
      headerName: 'Building Coverage',
      field: 'buildingCoverage',
      filter: filters.number,
    },
    {
      headerName: 'Calculated',
      field: 'calculated',
      filter: filters.set,
    },
    {
      headerName: 'Pronto Replacement Cost',
      field: 'prontoReplacementCost',
      filter: filters.number,
    },
    {
      headerName: 'ITV (%)',
      field: 'itv',
      filter: filters.number,
    },
    {
      headerName: 'Under Insured',
      field: 'underinsured',
      filter: filters.set,
    },
    {
      headerName: 'Over Insured',
      field: 'overinsured',
      filter: filters.set,
    },
    {
      headerName: 'Roof Issues',
      field: 'roofIssues',
      filter: filters.set,
    },
    {
      headerName: 'Betterview Roof Score',
      field: 'visualScore',
      filter: filters.number,
    },
    {
      headerName: 'Roof Issues Details',
      field: 'roofIssuesDetails',
      hide: true,
    },
    {
      headerName: 'Conditions Exposures',
      field: 'conditionsExposures',
      filter: filters.set,
    },
    {
      headerName: 'Detections',
      field: 'detections',
      hide: true,
    },
    {
      headerName: 'Permit Data/Renovations',
      field: 'permitData',
      filter: filters.set,
    },
    {
      headerName: 'Weather Risks',
      field: 'weatherRisk',
      filter: filters.set,
    },
    {
      headerName: 'Weather Risks Details',
      field: 'weatherRiskDetails',
      filter: filters.set,
      hide: true,
    },
    {
      headerName: 'Fire Protection',
      field: 'fireProtection',
      filter: filters.set,
    },
    {
      headerName: 'Defensible Space',
      field: 'defensibleSpace',
      filter: filters.set,
    },
    {
      headerName: 'Defensible Percent Zone 1',
      field: 'defensiblePercentZone1',
      filter: filters.number,
    },
    {
      headerName: 'Defensible Percent Zone 2',
      field: 'defensiblePercentZone2',
      filter: filters.number,
    },
    {
      headerName: 'Defensible Percent Zone 3',
      field: 'defensiblePercentZone3',
      filter: filters.number,
    },
    {
      headerName: 'Order #',
      field: 'orderLineId',
    },
    {
      headerName: 'Client Code',
      field: 'clientCode',
      filter: filters.set,
    },
    {
      headerName: 'Agency Code',
      field: 'agencyCode',
    },
    {
      headerName: 'Agency Name',
      field: 'agencyName',
    },
    {
      headerName: 'Coverage A',
      field: 'coverageA',
      filter: filters.number,
    },
    {
      headerName: 'Customer Ref #',
      field: 'customerReferenceNumber',
    },
  ], []);

  return (
    <AgGrid
      gridData={gridData}
      columnDefs={columnDefinitions}
      onGridReady={onGridReady}
      gridOptions={{
        rowSelection: 'multiple',
        frameworkComponents: {
          VirtualOrderPdfCellRenderer,
          BetterviewLinkCellRenderer,
          VirtualOrderConversionCellRenderer,
        },
        isRowSelectable: rowNode => (rowNode.data ? !rowNode.data.childOrderCreated : false),
      }}
      exportParams={{
        columnKeys: columnDefinitions
          .map(col => col.field)
          .filter(
            field =>
              !!field &&
              !['pdf', 'betterview', 'conversion'].includes(field),
          ),
      }}
      showLocalGridExport
      extraGridProps={{
        defaultExportParams: {
          allColumns: true,
        },
      }}
      headerContent={
        <div className={customActionsContainer}>
          <QuickVueVirtualToInvisionConversionButton
            disabled={!gridApi?.getSelectedRows().length}
            onClick={onOpenConversionForm}
            onHide={onHideConversionForm}
            orders={conversionOrders}
            showDialog={!!conversionOrders.length}
          >
            Convert Order(s)
          </QuickVueVirtualToInvisionConversionButton>
        </div>
      }
    />
  );
};

export default BetterviewGrid; 